import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadingIcon from "@mui/icons-material/Downloading";
import { DatePicker } from "@mui/x-date-pickers";
import moment, { Moment } from "moment";
import { toast } from "react-toastify";
import shiftManagementService from "services/shiftManagementService";
import yardService from "services/yardService";
import { ACTION_BUTTON_SX, SAVE_BUTTON_SX } from "shifts/shiftStyles";
import { useShiftPermissions } from "shifts/permissions";
import { ShiftHubOption } from "shifts/models";
import { YardWeekInstance, YardWeekRow } from "yard/models";

// Yard Daily — the week of concrete shift instances for a yard. Replaces the legacy
// Logistrics YardDaily page.
//
// Hub and week live in the query string so returning from an instance detail lands you back
// on the same week, which the legacy page achieved with its refer/hub/date round-trip.

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const YardDaily = () => {
  const navigate = useNavigate();
  const { canEditYardDaily } = useShiftPermissions();
  const [params, setParams] = useSearchParams();

  const [hubs, setHubs] = useState<ShiftHubOption[]>([]);
  const [hubId, setHubId] = useState<number | "">(params.get("hub") ? Number(params.get("hub")) : "");
  const [weekOf, setWeekOf] = useState<string>(params.get("date") ?? moment().format("YYYY-MM-DD"));

  const [rows, setRows] = useState<YardWeekRow[]>([]);
  const [weekStart, setWeekStart] = useState<string>("");
  const [canImport, setCanImport] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    shiftManagementService
      .getHubs()
      .then(setHubs)
      .catch(() => toast.error("Could not load yards."));
  }, []);

  const load = useCallback(async () => {
    if (hubId === "") {
      toast.warn("Pick a yard first.");
      return;
    }

    setIsLoading(true);
    try {
      const result = await yardService.getWeek(Number(hubId), weekOf);
      setRows(result.rows ?? []);
      setWeekStart(result.weekStart);
      setCanImport(result.canImportTemplate);
      setHasSearched(true);
      setParams({ hub: String(hubId), date: weekOf }, { replace: true });
    } catch {
      toast.error("Could not load the week.");
    } finally {
      setIsLoading(false);
    }
  }, [hubId, weekOf, setParams]);

  const runImport = async () => {
    setIsImporting(true);
    try {
      const result = await yardService.importWeek(Number(hubId), weekOf);
      if (result.success) {
        toast.success(result.message);
        await load();
      } else {
        toast.error(result.message);
      }
    } catch {
      toast.error("Could not import the master schedule.");
    } finally {
      setIsImporting(false);
    }
  };

  // "Back to Yard Daily" returns with hub and date already in the query string, so load that
  // week straight away — otherwise you land on a pre-filled form with an empty grid and have
  // to press Get Week again. Guarded to fire once: after this, loading stays button-driven,
  // so changing the yard or week still waits for Get Week.
  const didAutoLoad = useRef(false);
  useEffect(() => {
    if (didAutoLoad.current) return;
    if (hubId === "" || !params.get("hub") || !params.get("date")) return;
    didAutoLoad.current = true;
    load();
  }, [hubId, params, load]);

  const runSearch = async () => {
    if (!search.trim()) return;
    try {
      const result = await yardService.findInstance(search.trim());
      if (!result.found) {
        toast.error(`"${search.trim()}" does not exist.`);
        return;
      }
      navigate(`/shifts/logistrics/daily/${result.yardDailyId}?hub=${hubId}&date=${weekOf}`);
    } catch {
      toast.error("Could not run that search.");
    }
  };

  const openInstance = (instance: YardWeekInstance) =>
    navigate(`/shifts/logistrics/daily/${instance.yardDailyId}?hub=${hubId}&date=${weekOf}`);

  const weekDates = weekStart
    ? Array.from({ length: 7 }, (_, i) => moment(weekStart).add(i, "day"))
    : [];

  return (
    <Box sx={{ px: 1 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Yard Daily
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="daily-hub-label">Yard</InputLabel>
            <Select
              labelId="daily-hub-label"
              label="Yard"
              value={hubId}
              onChange={(e) => setHubId(e.target.value as number)}
            >
              {hubs.map((hub) => (
                <MenuItem key={hub.hubId} value={hub.hubId}>
                  {hub.location}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DatePicker
            label="Week of"
            value={moment(weekOf)}
            onChange={(value: Moment | null) => setWeekOf((value ?? moment()).format("YYYY-MM-DD"))}
            slotProps={{ textField: { size: "small", sx: { width: 175 } } }}
          />

          <Button variant="contained" color="info" onClick={load} disabled={isLoading}>
            Get Week
          </Button>

          {/* Importing creates a week of real shifts, so it needs edit rights -- a viewer
              gets the week read-only rather than an action that would populate it. */}
          {hasSearched && canImport && canEditYardDaily && (
            <Tooltip title="Creates this week's shifts from the yard's weekly template.">
              <Button
                variant="contained"
                startIcon={<DownloadingIcon />}
                sx={SAVE_BUTTON_SX}
                onClick={runImport}
                disabled={isImporting}
              >
                {isImporting ? "Importing…" : "Import Master Schedule"}
              </Button>
            </Tooltip>
          )}

          <Box sx={{ flex: 1 }} />

          <TextField
            label="Shift # or Control #"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") runSearch();
            }}
            sx={{ width: 210 }}
          />
          <Button variant="contained" sx={ACTION_BUTTON_SX} onClick={runSearch} disabled={!search.trim()}>
            Find
          </Button>
        </Stack>

        {weekStart && (
          <Typography variant="body2" sx={{ mt: 1.5, color: "text.secondary" }}>
            Week of {moment(weekStart).format("MM/DD/YYYY")}
            {!canImport && " · already built from the master schedule"}
          </Typography>
        )}
      </Paper>

      <Paper sx={{ overflow: "auto" }}>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 170 }}>Shift Name</TableCell>
                <TableCell sx={{ width: 90 }}>Short Key</TableCell>
                {DAY_NAMES.map((day, i) => (
                  <TableCell key={day} align="center" sx={{ minWidth: 130 }}>
                    {day}
                    {weekDates[i] && (
                      <Typography variant="caption" display="block" sx={{ color: "text.secondary" }}>
                        {weekDates[i].format("MM/DD")}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    {hasSearched ? "No shifts for this yard and week." : "Pick a yard and week, then Get Week."}
                  </TableCell>
                </TableRow>
              )}

              {rows.map((row) => (
                <TableRow key={row.shiftName} hover>
                  <TableCell>
                    {row.shiftName}
                    {row.isAdHoc && (
                      <Chip label="Ad Hoc" size="small" variant="outlined" sx={{ ml: 1, height: 18, fontSize: ".7em" }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{row.shortKey}</TableCell>
                  {row.days.map((cell) => (
                    <TableCell key={cell.dayOfWeek} align="center" sx={{ verticalAlign: "top" }}>
                      {cell.instances.map((instance) => (
                        <InstanceCell key={instance.yardDailyId} instance={instance} onOpen={openInstance} />
                      ))}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

const InstanceCell = ({
  instance,
  onOpen,
}: {
  instance: YardWeekInstance;
  onOpen: (instance: YardWeekInstance) => void;
}) => {
  const statusColor =
    instance.status === "Cancelled" ? "error.main" : instance.status === "Closed" ? "text.secondary" : "inherit";

  return (
    <Box
      onClick={() => onOpen(instance)}
      sx={{
        cursor: "pointer",
        borderRadius: 1,
        px: 0.5,
        py: 0.25,
        mb: 0.5,
        "&:hover": { backgroundColor: "action.hover" },
        textDecoration: instance.status === "Cancelled" ? "line-through" : "none",
      }}
    >
      <Typography variant="caption" display="block" sx={{ color: statusColor }}>
        {instance.driverName || "(no driver)"}
      </Typography>
      <Typography variant="caption" display="block" sx={{ color: "text.secondary" }}>
        {instance.unitNumber || "—"} · {instance.timeStart}
      </Typography>
      <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ mt: 0.25 }}>
        {instance.isApproved && <Chip label="Approved" size="small" color="success" sx={CHIP_SX} />}
        {instance.status === "Cancelled" && <Chip label="Cancelled" size="small" color="error" sx={CHIP_SX} />}
        {/* BNSF deactivated their side without cancelling ours — surfaced, not reconciled. */}
        {instance.isBnsfInactive && <Chip label="BNSF inactive" size="small" color="warning" sx={CHIP_SX} />}
      </Stack>
    </Box>
  );
};

const CHIP_SX = { height: 16, fontSize: ".62em", "& .MuiChip-label": { px: 0.6 } };

export default YardDaily;
